import { Modal } from "antd";
import useQueryBuildingsValidate from "ApiLayer/Building/queryBuildingsValidate";
import { Button } from "components/atoms/Button";
import LoadingSpinner from "components/atoms/LoadingSpinner/LoadingSpinner";
import { Paragraph } from "components/atoms/Typography";
import useAlert from "hooks/useAlert";
import useMutationCreateBuildings from "ApiLayer/Building/mutationCreateBuildings";
import posthog from "posthog-js";
import { useEffect } from "react";
import styles from "./CreateBuildingsModal.module.scss";
import ResultCards from "./ResultCards";
import InvalidBuildingsList from "./InvalidBuildingsList";

interface CreateBuildingsModalProps {
    modalIsOpen: boolean;
    buildingModelsInput: any;
    setModalIsOpen: (isOpen: boolean) => void;
}

function CreateBuildingsModal({ modalIsOpen, buildingModelsInput, setModalIsOpen }: CreateBuildingsModalProps) {
    const alert = useAlert();
    const { data, isLoading } = useQueryBuildingsValidate(buildingModelsInput);
    const { mutate: createBuildingModels, isLoading: isSubmitting } = useMutationCreateBuildings();
    useEffect(() => {
        if (!isLoading) posthog.capture("Buildings validated");
    }, [isLoading]);

    const handleSubmit = () => {
        if (!data?.validBuildings || !buildingModelsInput) return;

        createBuildingModels(data?.validBuildings || [], {
            onSuccess: () => {
                setModalIsOpen(false);
                posthog.capture("Buildings CSV submitted", {
                    added_buildings_count: buildingModelsInput.length,
                });
                alert.success("Your buildings have been submitted. Please allow up to several minutes.");
            },
        });
    };

    const handleCancel = () => setModalIsOpen(false);

    return (
        <Modal
            open={modalIsOpen}
            onCancel={handleCancel}
            title="New Buildings"
            className={styles.modalContainer}
            footer={
                <CreateBuildingsModalFooter
                    onSave={() => handleSubmit()}
                    onCancel={handleCancel}
                    isLoading={isLoading || isSubmitting}
                />
            }
        >
            {isLoading || isSubmitting ? (
                <LoadingSpinner />
            ) : (
                <div className={styles.container}>
                    <Paragraph>
                        Building verification complete. <br /> Review details below - only valid buildings will be
                        created.
                    </Paragraph>
                    <ResultCards
                        buildingsSubmitted={buildingModelsInput?.length || 0}
                        buildingsInvalid={data?.invalidBuildings.length || 0}
                        buildingsValid={data?.validBuildings.length || 0}
                    />
                    <InvalidBuildingsList invalidBuildings={data?.invalidBuildings || []} />
                </div>
            )}
        </Modal>
    );
}

interface CreateBuildingsModalFooterProps {
    onSave: () => void;
    onCancel: () => void;
    isLoading: boolean;
}

function CreateBuildingsModalFooter({ onSave, onCancel, isLoading }: CreateBuildingsModalFooterProps) {
    return (
        <div className="bulk-edit-tags__footer">
            <Button type="primary" onClick={onSave} disabled={isLoading}>
                Create
            </Button>
            <Button type="link" onClick={onCancel} style={{ color: "var(--audette-black)" }} disabled={isLoading}>
                Cancel
            </Button>
        </div>
    );
}

export default CreateBuildingsModal;
